import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, CheckCircle2, Unplug, Loader2, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";

// The creator's own OpenAI key: powers comment polishing and the AI voice.
export function OpenAIKeyCard() {
  const [status, setStatus] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => api.get("/openai/status").then((r) => setStatus(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/openai/key", { api_key: apiKey.trim() });
      toast.success("OpenAI key checked and saved");
      setApiKey("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save this key");
    }
    setSaving(false);
  };
  const remove = async () => {
    await api.post("/openai/disconnect");
    toast.success("OpenAI key removed");
    load();
  };

  return (
    <section className="bg-white border border-zinc-200 shadow-sm rounded-[4px] p-7 mb-6" data-testid="openai-key-card">
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-display font-semibold flex items-center gap-2 text-zinc-900"><Sparkles size={16} className="text-rose-500" /> OpenAI API key</h2>
        {status?.connected
          ? <span className="text-xs text-amber-700 border border-amber-300 bg-amber-50 rounded-full px-3 py-1 flex items-center gap-1.5" data-testid="openai-status-pill"><CheckCircle2 size={13} /> Connected</span>
          : <span className="text-xs text-zinc-500 border border-zinc-300 bg-zinc-50 rounded-full px-3 py-1" data-testid="openai-status-pill">Not connected</span>}
      </div>
      <p className="text-xs text-zinc-500 leading-relaxed mb-5">
        Polishing your comments and the AI voice run on <span className="text-zinc-800">your own OpenAI key</span>, billed to you by OpenAI. We check the key before saving and store it encrypted.
      </p>
      <form onSubmit={save} className="flex flex-col sm:flex-row gap-2 mb-4">
        <input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} autoComplete="off" data-testid="openai-key-input"
          placeholder={status?.connected ? "Paste a new sk-… key to replace it" : "sk-…"}
          className="flex-1 bg-white border border-zinc-300 rounded-[4px] px-4 py-2.5 text-sm text-zinc-900 placeholder:text-zinc-400 outline-none focus:ring-2 focus:ring-rose-500" />
        <button type="submit" disabled={saving || !apiKey.trim()} data-testid="openai-save-btn"
          className="bg-zinc-900 hover:bg-zinc-800 disabled:opacity-40 text-white text-sm font-medium rounded-[4px] px-6 py-2.5 transition-colors flex items-center justify-center gap-2">
          {saving && <Loader2 size={14} className="animate-spin" />} Save key
        </button>
      </form>
      <div className="flex items-center justify-between gap-4">
        <a href="https://platform.openai.com/api-keys" target="_blank" rel="noreferrer" data-testid="openai-get-key-link"
          className="text-xs text-rose-600 hover:text-rose-700 underline inline-flex items-center gap-1">Get an OpenAI API key <ExternalLink size={11} /></a>
        {status?.connected && (
          <button type="button" onClick={remove} data-testid="openai-disconnect-btn" className="text-xs text-zinc-500 hover:text-rose-500 flex items-center gap-1.5 transition-colors"><Unplug size={13} /> Remove key</button>
        )}
      </div>
    </section>
  );
}
